import { useState, useEffect } from 'react';
import { ArrowDown } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { StickyHeader } from './StickyHeader';

const HeroSection = () => {
  const [count, setCount] = useState(0);
  
  useEffect(() => {
    const interval = setInterval(() => {
      if (count < 25000) {
        setCount(prev => Math.min(prev + 500, 25000));
      } else {
        clearInterval(interval);
      }
    }, 30);
    
    return () => clearInterval(interval);
  }, [count]);

  const scrollToContact = () => {
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };
  
  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <section className="min-h-screen flex flex-col justify-center relative overflow-hidden pt-16">
      <motion.div 
        className="absolute inset-0 bg-gradient-to-br from-woost-soft-blue/30 via-white to-woost-soft-yellow/30 pointer-events-none"
        animate={{
          backgroundPosition: ['0% 0%', '100% 100%'],
        }}
        transition={{
          duration: 20,
          repeat: Infinity,
          repeatType: "reverse",
          ease: "linear"
        }}
      />

      <StickyHeader />

      <div className="container mx-auto px-4 md:px-6 py-12 md:py-24 relative z-10">
        <motion.div 
          className="flex flex-col md:flex-row md:items-center"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <div className="md:w-3/5 space-y-6 md:pr-8">
            <motion.h1 
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-woost-black leading-tight"
              variants={itemVariants}
            >
              India's #1 ROI-Driven Influencer Marketing Platform
            </motion.h1>
            <motion.p 
              className="text-xl md:text-2xl text-gray-700"
              variants={itemVariants}
            >
              From Barter to UGC, Woost helps you run high-impact campaigns with 25,000+ nano & micro creators.
            </motion.p>
            
            <motion.div 
              className="pt-6 flex flex-col sm:flex-row gap-6"
              variants={itemVariants}
            >
              <div className="flex flex-col items-center sm:items-start">
                <Button 
                  onClick={scrollToContact}
                  className="bg-woost-blue hover:bg-woost-blue-dark text-white px-8 py-6 rounded-xl text-lg w-full sm:w-auto"
                >
                  Get in Touch <ArrowDown size={18} className="ml-2" />
                </Button>
                <span className="text-sm text-gray-600 mt-2">For Brands</span>
              </div>
              
              <div className="flex flex-col items-center sm:items-start">
                <div className="flex items-center gap-3 justify-center">
                  <a 
                    href="https://play.google.com/store/apps/details?id=com.woost" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className=" text-white  py-2 rounded-lg h-12 flex items-center justify-center gap-2 transition-colors"
                  >
                    {/* <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
                      <path d="M5.25 2a.75.75 0 0 1 .75.75v18.5a.75.75 0 0 1-.75.75h-3a.75.75 0 0 1-.75-.75V2.75A.75.75 0 0 1 2.25 2h3ZM6 12l7.5-7.5v15L6 12Zm11.25-9.5a.75.75 0 0 0-.75.75v18.5c0 .414.336.75.75.75h3a.75.75 0 0 0 .75-.75V3.25a.75.75 0 0 0-.75-.75h-3Z" />
                    </svg>
                    <div className="flex flex-col items-start">
                      <span className="text-xs">GET IT ON</span>
                      <span className="text-sm font-semibold">Google Play</span>
                    </div> */}
                    <img src="/images/playstore.png" alt="playstore logo" className='h-12'/>
                  </a>
                  <a 
                    href="https://apps.apple.com/in/app/woost-influencer-marketing/id6461419665" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className=" text-white  py-2 rounded-lg h-12 flex items-center justify-center gap-2 transition-colors"
                  >
                    {/* <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
                      <path d="M16.83 7.304c.089.01.178.01.266.01 1.271 0 2.759-.679 3.525-1.96a4.96 4.96 0 0 0-3.761-1.853c-.089 0-.178.01-.266.01-.198.01-.396.02-.585.05-1.366.207-2.504.92-3.247 1.95.909.712 2.245 1.594 4.068 1.793ZM14.415 9.334c-.455-.49-.792-.991-1.01-1.494a3.028 3.028 0 0 1-.217-.614h-.01c-.099-.336-.139-.663-.139-.98 0-.306.04-.603.119-.87.04-.138.089-.267.149-.395.03-.69.06-.138.099-.198a1.26 1.26 0 0 1 .02-.049c-.683-.702-1.603-1.225-2.76-1.355-.198-.02-.406-.03-.603-.03-1.465 0-2.769.623-3.672 1.644a4.845 4.845 0 0 0-1.306 3.37c0 .148.01.306.03.455.01.05.01.108.02.158.01.049.01.099.02.148.01.079.02.158.04.237.01.069.03.148.05.217.108.455.267.9.475 1.335.208.435.466.87.773 1.286.257.345.554.691.881 1.037.337.345.703.681 1.1 1.007.366.306.752.594 1.167.861.415.267.851.514 1.306.732.03.02.06.03.099.049-.989-.395-1.978-.752-2.967-1.057a.75.75 0 0 0-.955.693c-.01.119.01.238.069.346.97 1.88 2.275 3.36 3.81 4.389 1.01.67 2.12 1.057 3.29 1.057a5.7 5.7 0 0 0 .415-.02h.01a.77.77 0 0 0 .514-.247.76.76 0 0 0 .228-.524c0-.04 0-.08-.01-.119-2.473-2.592-4.068-5.707-4.789-9.293 1.216.356 2.432.96 3.639 1.812.98.702 1.86 1.563 2.63 2.572.198.257.376.524.554.8.198.307.386.614.554.94.99.178.188.357.267.535.89.187.168.375.237.562.109.296.198.583.267.87.02.099.04.188.05.277.59.288.198.445.425.445.247 0 .465-.177.505-.425v-.049c.04-.277.08-.544.099-.81 0-.4.01-.79.01-.119 0-.03 0-.06-.01-.089v-.03c0-.049-.01-.099-.01-.148-.01-.089-.01-.178-.02-.267-.01-.089-.02-.168-.03-.257-.01-.089-.03-.168-.04-.247-.02-.089-.03-.168-.04-.247-.01-.079-.03-.158-.05-.237-.02-.08-.03-.158-.05-.238a4.54 4.54 0 0 0-.139-.504c-.04-.129-.089-.257-.139-.386a6.39 6.39 0 0 0-.346-.752c-.139-.257-.297-.514-.455-.761a7.68 7.68 0 0 0-.584-.781 9.226 9.226 0 0 0-.673-.761Z" />
                    </svg> */}
                    <img src="/images/appstore.png" alt="appstore logo" className='h-12 rounded-md'/>
                    {/* <div className="flex flex-col items-start">
                      <span className="text-xs">Download on the</span>
                      <span className="text-sm font-semibold">App Store</span>
                    </div> */}
                  </a>
                </div>
                <div className="flex items-center mt-2">
                  <span className="text-sm text-gray-600">For Creators</span>
                  <span className="ml-2 px-2 py-1 bg-woost-soft-green text-green-700 text-xs font-semibold rounded-full">500+ creators joined this week</span>
                </div>
              </div>
            </motion.div>
            
            <motion.div 
              variants={itemVariants}
              className="mt-4 flex items-center gap-4 bg-gray-50 p-3 rounded-lg"
            >
              <div className="flex items-center">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} className="w-4 h-4 text-yellow-400 fill-current" viewBox="0 0 24 24">
                    <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                  </svg>
                ))}
                <span className="ml-1 font-medium">4.2</span>
              </div>
              <div className="text-sm text-gray-600">
                Trusted by <span className="font-semibold text-woost-purple">{count.toLocaleString()}+</span> creators
              </div>
            </motion.div>
          </div>
          
          <div className="mt-10 md:mt-0 md:w-2/5 flex justify-center">
            <motion.div 
              variants={itemVariants}
              className="relative"
            >
              <motion.div 
                className="absolute -top-4 -left-4 w-24 h-24 bg-woost-yellow rounded-lg rotate-12"
                animate={{
                  rotate: [12, 8, 12],
                  y: [0, -10, 0]
                }}
                transition={{
                  duration: 5,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              />
              <motion.div 
                className="absolute -bottom-4 -right-4 w-32 h-32 bg-woost-soft-green rounded-lg -rotate-6"
                animate={{
                  rotate: [-6, -10, -6],
                  y: [0, 10, 0]
                }}
                transition={{
                  duration: 5,
                  delay: 1,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              />
              
              <motion.div 
                className="relative z-10 rounded-3xl overflow-hidden  h-[500px] "
                whileHover={{ 
                  y: -10, 
                  transition: { duration: 0.2 } 
                }}
              >
                {/* <div className="h-full overflow-y-auto">
                  <div className="p-4 grid gap-4">
                    {[
                      { title: "BeautyBlend Campaign", amount: "₹15,000", desc: "Create Instagram Reels", brand: "SUGAR Cosmetics" },
                      { title: "Tech Review Series", amount: "₹20,000", desc: "Product Reviews", brand: "OnePlus" },
                      { title: "Food Vlog Series", amount: "₹12,000", desc: "Restaurant Reviews", brand: "Zomato" },
                      { title: "Fashion Lookbook", amount: "₹18,000", desc: "Instagram Posts", brand: "Myntra" },
                    ].map((campaign, index) => (
                      <div 
                        key={index} 
                        className="bg-gray-50 p-4 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                      >
                        <div className="flex justify-between items-start mb-2">
                          <h4 className="font-medium text-sm">{campaign.title}</h4>
                          <span className="text-woost-blue font-semibold text-sm">{campaign.amount}</span>
                        </div>
                        <p className="text-gray-600 text-xs mb-1">{campaign.desc}</p>
                        <p className="text-gray-500 text-xs">by {campaign.brand}</p>
                      </div>
                    ))}
                  </div>
                </div> */}
                <div>
                  <img src="/images/mm.png" alt="mock" />
                </div>
              </motion.div>
            </motion.div>
          </div>
        </motion.div>
      </div>
      
      <motion.div 
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        animate={{ 
          y: [0, 10, 0],
          opacity: [0.5, 1, 0.5]
        }}
        transition={{
          duration: 2,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      >
        <ArrowDown size={24} className="text-woost-blue" />
      </motion.div>
    </section>
  );
};

export default HeroSection;
